import { TRAVEL_CYCLE_TARGET_RATE } from "@shared/constants/travel-cycle-causes.enum";
import { TravelCyclePlaceRow } from "@shared/interfaces/travel-cycle-analysis.interface";

import {
  CHART_FILTERED_PLANTS,
  CHART_WORST_PLANTS,
  CYCLE_RATE_COLORS,
  CYCLE_WARNING_RATE,
  MIN_DAILY_TRAVELS,
  MIN_PLACE_TRAVELS
} from "./constants";

/** Share of `part` in `total`, in percent; 0 when there is nothing to divide. */
export const percentOf = (part: number, total: number): number => (total > 0 ? (100 * part) / total : 0);

/** Percentage with a comma, as the screen shows it: 76,3%. */
export const formatPercent = (value: number, digits = 1): string => `${value.toLocaleString("pt-BR", {
  minimumFractionDigits: digits,
  maximumFractionDigits: digits
})}%`;

/** Whole number with thousands separator: 29.164. */
export const formatCount = (value: number): string => value.toLocaleString("pt-BR");

/** Status color of a complete-cycle rate; gray when there are too few travels to judge. */
export const rateColor = (rate: number, travels = MIN_PLACE_TRAVELS): string => {
  if (travels < MIN_PLACE_TRAVELS) return CYCLE_RATE_COLORS.muted;
  if (rate >= TRAVEL_CYCLE_TARGET_RATE) return CYCLE_RATE_COLORS.ok;

  return rate >= CYCLE_WARNING_RATE ? CYCLE_RATE_COLORS.warning : CYCLE_RATE_COLORS.critical;
};

/**
 * Regionals or plants drawn in the daily chart. Every regional; for plants, the ones with enough travels and the
 * lowest rates: up to 10 when the screen is filtered by regional or plant, else the 8 worst.
 * @param rows Breakdown rows, already sorted worst first.
 * @param kind Regional or plant breakdown.
 * @param filteredByPlace The screen has a regional or plant filter.
 */
export const chartPlaces = (rows: TravelCyclePlaceRow[], kind: "regional" | "plant", filteredByPlace: boolean): TravelCyclePlaceRow[] => {
  const enough = rows.filter((row) => row.total >= MIN_PLACE_TRAVELS);

  if (kind === "regional") return enough;

  return enough.slice(0, filteredByPlace ? CHART_FILTERED_PLANTS : CHART_WORST_PLANTS);
};

/**
 * Complete-cycle rate per day, or null on days with too few travels to draw a point.
 * @param daily [total, complete] per day.
 */
export const dailyRates = (daily: [number, number][]): (number | null)[] => daily
  .map(([total, complete]) => (total >= MIN_DAILY_TRAVELS ? Math.round(percentOf(complete, total) * 10) / 10 : null));

/**
 * Trailing moving average of the daily rates, over the last `size` days that have a rate.
 * @param values Daily rates, null on days left out.
 * @param size Days in the window.
 */
export const movingAverage = (values: (number | null)[], size = 5): (number | null)[] => values.map((_value, i) => {
  const window = values.slice(Math.max(0, i - size + 1), i + 1).filter((v): v is number => v !== null);

  return window.length ? Math.round((window.reduce((sum, v) => sum + v, 0) / window.length) * 10) / 10 : null;
});

/**
 * Axis label of a local day: "05/10 seg".
 * @param day Local day as YYYY-MM-DD.
 * @param language Language of the weekday name.
 */
export const dayLabel = (day: string, language: string): string => {
  const weekday = new Date(`${day}T12:00:00`).toLocaleDateString(language, { weekday: "short" }).replace(".", "");

  return `${day.slice(8, 10)}/${day.slice(5, 7)} ${weekday}`;
};
